import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { STUDIO_INQUIRY_REVIEW, STUDIO_INQUIRY_STUDIO_OPERATIONS } from '../../constants/common';

/** One "Label: value" line of a review section */
interface ReviewLine {
  label?: string;
  value?: string;
  /** Values stacked under one another, as the primary contact is shown */
  lines?: string[];
  /** A red italic caveat printed beneath the value */
  note?: string;
}

/** A row of the review card: a heading, its data, and an Edit link */
interface ReviewSection {
  title: string;
  /** Dismiss reason the modal uses to reopen the step this section came from */
  target: string;
  /** Blocks of lines, spaced further apart than the lines within a block */
  groups: ReviewLine[][];
}

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'app-studio-inquiry-review',
    templateUrl: './studio-inquiry-review.component.html',
    styleUrls: ['./studio-inquiry-review.component.scss'],
    standalone: false
})
export class StudioInquiryReviewComponent implements OnInit {
  @Input() projectInformation: any;
  @Input() productionCompany: any;
  @Input() studioOperations: any;
  @Input() costumesRentals: any;
  @Output() inquirySubmitted = new EventEmitter<any>();
  constants = STUDIO_INQUIRY_REVIEW;
  sections: ReviewSection[] = [];

  constructor(public activeModal: NgbActiveModal) { }

  ngOnInit(): void {
    this.sections = _.compact([
      this.buildServices(),
      this.buildProject(),
      this.buildProductionCompany(),
      this.buildStudioOperations(),
      this.buildCostumesRentals(),
    ]);
  }

  /**
   * onEdit() - Reopens the step a section came from
   * @param target the section's dismiss reason
   */
  onEdit(target: string): void {
    this.activeModal.dismiss(target);
  }

  /**
   * onSubmit() - Hands the completed inquiry back and closes the modal
   */
  onSubmit(): void {
    const inquiry = {
      projectInformation: this.projectInformation,
      productionCompany: this.productionCompany,
      studioOperations: this.studioOperations,
      costumesRentals: this.costumesRentals,
    };
    this.inquirySubmitted.emit(inquiry);
    this.activeModal.close(inquiry);
  }

  /**
   * onBack() - Returns to the costumes rentals step
   */
  onBack(): void {
    this.activeModal.dismiss('back');
  }

  /**
   * closeModal() - Dismisses the inquiry
   */
  closeModal(): void {
    this.activeModal.dismiss();
  }

  /**
   * buildServices() - Lists the services picked on the first step
   */
  private buildServices(): ReviewSection {
    const services = _.map(this.projectInformation?.services, 'name');
    return {
      title: this.constants.services,
      target: 'edit:services',
      groups: [_.map(services, (name) => ({ value: name }))],
    };
  }

  /**
   * buildProject() - Summarises the project information step
   */
  private buildProject(): ReviewSection {
    return {
      title: this.constants.project,
      target: 'edit:project',
      groups: [_.compact([
        this.line(this.constants.projectType, this.projectInformation?.projectType),
        this.line(this.constants.projectName, this.projectInformation?.projectName),
        this.line(this.constants.startDate, this.formatDate(this.projectInformation?.startDate)),
        this.line(this.constants.shootDate, this.formatDate(this.projectInformation?.shootDate)),
        this.line(this.constants.endDate, this.formatDate(this.projectInformation?.endDate)),
      ])],
    };
  }

  /**
   * buildProductionCompany() - Summarises the production company step, keeping
   * the company, its contact and its address in separate blocks
   */
  private buildProductionCompany(): ReviewSection {
    const company = this.productionCompany;
    return {
      title: this.constants.productionCompany,
      target: 'edit:company',
      groups: [
        _.compact([this.line(this.constants.companyName, company?.companyName)]),
        _.compact([
          this.line(this.constants.contactName, this.fullName(company?.firstName, company?.lastName)),
          this.line(this.constants.role, company?.jobTitle),
          this.line(this.constants.email, company?.email),
          this.line(this.constants.phone, company?.phone),
          this.line(this.constants.preferred, company?.preferredContact),
        ]),
        _.compact([
          this.line(this.constants.address, this.formatAddress(company)),
          this.line(this.constants.phone, company?.addressPhone),
        ]),
      ].filter((group) => group.length),
    };
  }

  /**
   * buildStudioOperations() - Summarises the studio operations step
   */
  private buildStudioOperations(): ReviewSection {
    return {
      title: this.constants.studioOperations,
      target: 'edit:operations',
      groups: [
        _.compact([this.line(this.constants.location, this.formatLocation())]),
        _.compact([this.line(this.constants.officeSpace, this.studioOperations?.officeSpaceRequired)]),
      ].filter((group) => group.length),
    };
  }

  /**
   * buildCostumesRentals() - Summarises the costumes rentals step, which the
   * applicant is free to skip
   * @returns the section, or null when the step was skipped
   */
  private buildCostumesRentals(): ReviewSection | null {
    const rentals = this.costumesRentals;
    if (!rentals) {
      return null;
    }
    const contactLines = _.compact([
      rentals.primaryContactType,
      this.fullName(rentals.firstName, rentals.lastName),
      rentals.phone,
      rentals.cloudCheckIn === 'Yes' ? this.constants.cloudCheckInAccess : null,
    ]);
    const payment = this.line(this.constants.payment, rentals.paymentType);
    if (payment && rentals.paymentType === this.constants.lineOfCreditPayment) {
      payment.note = this.constants.lineOfCreditNote;
    }
    return {
      title: this.constants.costumesRentals,
      target: 'edit:costumes',
      groups: [_.compact([
        this.line(this.constants.pullingDate, this.formatDate(rentals.pullDate)),
        this.line(this.constants.racks, rentals.racks),
        this.line(this.constants.visitCollection, rentals.visitCollection),
        contactLines.length ? { label: this.constants.primaryContact, lines: contactLines } : null,
        this.line(this.constants.orderShipped, rentals.orderShipped),
        this.line(this.constants.billingAddress, this.formatAddress(rentals)),
        this.line(this.constants.billingPhone, rentals.addressPhone),
        this.line(this.constants.billingContact, this.joinContact(rentals)),
        payment,
      ])],
    };
  }

  /**
   * line() - Builds a review line, dropping the ones with nothing to show
   * @param label the bold prefix
   * @param value what the applicant entered
   * @returns the line, or null when the value is blank
   */
  private line(label: string, value: string): ReviewLine | null {
    return value ? { label, value } : null;
  }

  /**
   * fullName() - Joins a first and last name
   */
  private fullName(firstName: string, lastName: string): string {
    return _.compact([firstName, lastName]).join(' ');
  }

  /**
   * joinContact() - Renders the billing contact as "name; email"
   */
  private joinContact(rentals: any): string {
    return _.compact([rentals?.billingContactName, rentals?.billingEmail]).join('; ');
  }

  /**
   * formatAddress() - Renders the street, city, state and zip on one line
   */
  private formatAddress(source: any): string {
    return _.compact([source?.street, source?.city, source?.state, source?.zipCode]).join(', ');
  }

  /**
   * formatDate() - Renders a date the way the picker stored it, which delimits
   * the parts with dashes in whichever order the user prefers
   * @param value the stored date
   * @returns the same date delimited with slashes
   */
  private formatDate(value: string): string {
    return value ? _.replace(value, /-/g, '/') : '';
  }

  /**
   * formatLocation() - Names the locations chosen on the studio operations step,
   * listing the individual exterior sets beside their heading
   */
  private formatLocation(): string {
    const operations = this.studioOperations;
    const chosen: string[] = [];
    if (operations?.soundStages) {
      chosen.push(this.constants.soundStages);
    }
    if (operations?.exteriorSets) {
      const sets = _.filter(
        STUDIO_INQUIRY_STUDIO_OPERATIONS.exteriorSetOptions,
        (option) => operations?.exteriorSetSelections?.[option.id]
      );
      const names = _.map(sets, 'name').join(', ');
      chosen.push(names ? `${this.constants.exteriorSets} (${names})` : this.constants.exteriorSets);
    }
    return chosen.join(', ');
  }
}
